/**
 * Activities Component
 *
 * Displays a list of activities required to finish a lesson, including video, quiz, homework, and peer evaluation.
 * Handles navigation between lesson sections and displays support material if available.
 *
 * Features:
 * - Renders activity buttons for each required event type (video, quiz, homework, peer evaluation)
 * - Shows progress and completion status for each activity
 * - Handles navigation and updates browser history for deep-linking
 * - Displays support material links if provided
 * - Uses Material-UI for UI elements
 *
 * @param {Object} props - Component properties
 * @param {Array} [props.material] - Array of support material objects
 * @param {Array} [props.lessonData] - Array of lesson data objects
 * @param {Array} [props.lessonProgress] - Array of lesson progress objects
 * @param {function} [props.changeView] - Function to change the current view
 * @param {number} [props.videoProgress] - Progress status for video activity
 * @param {number} [props.quizProgress] - Progress status for quiz activity
 * @param {number} [props.homeworkProgress] - Progress status for homework activity
 * @param {number} [props.coevaluationProgress] - Progress status for peer evaluation activity
 * @param {string} [props.format] - Format of the lesson (e.g., 'AUDIO', 'VIDEO')
 * @returns {JSX.Element} Rendered Activities component with activity buttons and support material
 */

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import LinkIcon from '@mui/icons-material/Link';
import DoneIcon from '@mui/icons-material/Done';

import classes from './Activities.module.css';

const Activities = props => {
  const url = window.location.pathname.split('/');
  const { t } = useTranslation(['lesson']);

  /**
   * Handles navigation between lesson sections and updates browser history
   * @param {number} newValue - New view index to navigate to
   */
  const changeViewHandler = newValue => {
    props.changeView(newValue);
    let section = '';
    if (newValue === 1) {
      section = props.format === 'AUDIO' ? 'aud' : 'f';
    } else if (newValue === 2) {
      section = 'q';
    } else if (newValue === 3) {
      section = 'ac';
    } else {
      section = 'coe';
    }
    // Update URL for navigation
    if (url[2] === 'a' || url[2] === 'course') {
      history.replaceState(
        null,
        null,
        `/${url[1]}/${url[2]}/${section}/${url[4]}/${url[5]}`
      );
    } else {
      history.replaceState(
        null,
        null,
        `/${url[1]}/${section}/${url[3]}/${url[4]}`
      );
    }
    props.changeView(newValue);
  };

  return (
    <div>
      {/* Title for activities section */}
      <p className={classes.text}>{t('activities_to_finish')}</p>
      {/* Render activity buttons for each progress event type */}
      {props.lessonProgress.map(progress => (
        <>
          {progress.eventType === 1 && (
            <Button
              onClick={() => changeViewHandler(1)}
              className={classes.activityBtn}
            >
              {/* Custom prop: 'className' for icon styling */}
              <DoneIcon
                className={`${classes.doneIcon} ${
                  progress.mediaStatus === 2 && classes.success
                } ${
                  (progress.mediaStatus === 1 ||
                    props.videoProgress) &&
                  classes.progress
                }`}
              />{' '}
              {props.format === 'AUDIO' ? 'Audio' : t('video')}
            </Button>
          )}
          {progress.eventType === 6 && (
            <Button
              onClick={() => changeViewHandler(2)}
              className={classes.activityBtn}
            >
              <DoneIcon
                className={`${classes.doneIcon} ${
                  (progress.mediaStatus === 2 ||
                    props.quizProgress === 2) &&
                  classes.success
                } ${
                  (progress.mediaStatus === 1 ||
                    props.quizProgress === 1) &&
                  classes.progress
                }`}
              />{' '}
              {t('assessment')}
            </Button>
          )}
          {progress.eventType === 3 && (
            <Button
              onClick={() => changeViewHandler(3)}
              className={classes.activityBtn}
            >
              <DoneIcon
                className={`${classes.doneIcon} ${
                  (progress.mediaStatus === 2 ||
                    props.homeworkProgress === 2) &&
                  classes.success
                } ${progress.mediaStatus === 1 && classes.progress}`}
              />{' '}
              {t('activity')}
            </Button>
          )}
          {progress.eventType === 5 && (
            <Button
              onClick={() => changeViewHandler(4)}
              className={classes.activityBtn}
            >
              <DoneIcon
                className={`${classes.doneIcon} ${
                  props.coevaluationProgress >= 3 && classes.success
                } ${
                  props.coevaluationProgress > 0 && classes.progress
                }`}
              />{' '}
              {t('peer_evaluation')}
              <span className={classes.coevaluationText}>
                ({props.coevaluationProgress} / 3)
              </span>
            </Button>
          )}
        </>
      ))}

      {/* Support material section */}
      {props.material &&
        (props.material.length > 0 ? (
          <>
            <p className={classes.text}>{t('support_material')}</p>
            <ul className={classes.materialList}>
              {props.material.map(item => (
                <li
                  key={item.idArchivoVideo}
                  className={classes.material}
                >
                  <a
                    className={classes.materialLink}
                    href={item.path}
                    target="_blank"
                    rel="noReferrer"
                  >
                    <LinkIcon className={classes.materiaIcon} />{' '}
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className={classes.text}>{t('no_support_material')}</p>
        ))}
    </div>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 * Provides runtime validation and serves as inline documentation
 */
Activities.propTypes = {
  /** Array of support material objects */
  material: PropTypes.array,
  /** Array of lesson data objects */
  lessonData: PropTypes.array,
  /** Array of lesson progress objects */
  lessonProgress: PropTypes.array,
  /** Function to change the current view */
  changeView: PropTypes.func,
  /** Progress status for video activity */
  videoProgress: PropTypes.number,
  /** Progress status for quiz activity */
  quizProgress: PropTypes.number,
  /** Progress status for homework activity */
  homeworkProgress: PropTypes.number,
  /** Progress status for peer evaluation activity */
  coevaluationProgress: PropTypes.number,
  /** Format of the lesson (e.g., 'AUDIO', 'VIDEO') */
  format: PropTypes.string
};

Activities.defaultProps = {
  material: [],
  lessonData: [],
  lessonProgress: [],
  changeView: undefined,
  videoProgress: undefined,
  quizProgress: undefined,
  homeworkProgress: undefined,
  coevaluationProgress: undefined,
  format: undefined
};

export default Activities;
